import * as React from "react";

import { useInterval } from "./use-interval";
import { unsafe_useLayoutEffectWithoutWarning as useLayoutEffect } from "./use-layout-effect";

const DEFAULT_DELAY_MS = 500;
const DEFAULT_SPEED_PX_PER_FRAME = 6;
const DEFAULT_INTERVAL_MS = 10;

interface UseAutoScrollOnHoverOptions {
  getIframe: () => HTMLIFrameElement | null;
  delayMs?: number;
  speedPxPerFrame?: number;
  intervalMs?: number;
}

export function useAutoScrollOnHover({
  getIframe,
  delayMs = DEFAULT_DELAY_MS,
  speedPxPerFrame = DEFAULT_SPEED_PX_PER_FRAME,
  intervalMs = DEFAULT_INTERVAL_MS,
}: UseAutoScrollOnHoverOptions) {
  const scrollPositionRef = React.useRef(0);
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [isScrolling, setIsScrolling] = React.useState(false);

  const getIframeRef = React.useRef(getIframe);
  useLayoutEffect(() => {
    getIframeRef.current = getIframe;
  }, [getIframe]);

  useInterval(
    () => {
      const iframe = getIframeRef.current();
      if (!iframe?.contentWindow) {
        return;
      }

      // NOTE (Ezra, 2024-12-29): Wrap in try-catch to handle cross-origin iframes.
      // When content isn't same-origin, contentWindow access throws a SecurityError.
      try {
        const doc = iframe.contentWindow.document;
        if (!doc.documentElement) {
          setIsScrolling(false);
          return;
        }
        const maxScroll =
          doc.documentElement.scrollHeight - doc.documentElement.clientHeight;

        if (scrollPositionRef.current >= maxScroll) {
          setIsScrolling(false);
          return;
        }

        scrollPositionRef.current += speedPxPerFrame;
        iframe.contentWindow.scrollTo(0, scrollPositionRef.current);
      } catch (error) {
        if (error instanceof DOMException && error.name === "SecurityError") {
          setIsScrolling(false);
        } else {
          throw error;
        }
      }
    },
    isScrolling ? intervalMs : null,
  );

  const clearPendingTimeout = React.useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => clearPendingTimeout, [clearPendingTimeout]);

  const startScrolling = React.useCallback(() => {
    clearPendingTimeout();
    scrollTimeoutRef.current = setTimeout(() => setIsScrolling(true), delayMs);
  }, [delayMs, clearPendingTimeout]);

  const stopScrolling = React.useCallback(() => {
    clearPendingTimeout();
    setIsScrolling(false);
    scrollPositionRef.current = 0;
    try {
      const iframe = getIframeRef.current();
      iframe?.contentWindow?.scrollTo(0, 0);
    } catch (error) {
      if (error instanceof DOMException && error.name === "SecurityError") {
        // Cross-origin iframe — scroll reset not possible, but that's fine.
      } else {
        throw error;
      }
    }
  }, [clearPendingTimeout]);

  return { startScrolling, stopScrolling };
}
